import React from 'react';
import SparkMD5 from 'spark-md5';
import uuid from 'uuid';

import firebase from '../../utils/firebase';

const importStorage = firebase.app().storage('gs://import.storage.commentbox.io');

function getElementContentByTag(parent, tag, required = false) {

    const elements = parent.getElementsByTagName(tag);
    if (required && !elements[0]) {
        throw new Error(`No child tag "${tag}" found`);
    } else if (!elements[0]) {
        return '';
    }

    const content = elements[0].textContent;
    if (required && !content) {
        throw new Error('Required content not found');
    }

    return content;
}

function getRequiredAttribute(element, attribute) {

    if (!element) {
        throw new Error('Required element not found');
    }

    const value = element.getAttribute(attribute);
    if (!value) {
        throw new Error('Required attribute not found');
    }

    return value;
}

class Import extends React.Component {

    state = {
        file: null,
        boxId: '',
        errorMessage: '',
        commentsToUpload: null,
        statuses: null
    };

    onChangeFile = ({ currentTarget: { files } }) => {

        if (files === undefined) {
            return this.setState({ errorMessage: 'Importing requires browser support for the File API, found in modern browsers.' });
        }

        const [ file ] = files;

        this.setState({
            file,
            errorMessage: '',
            commentsToUpload: null,
            statuses: null
        });
    };

    onChangeBoxId = ({ currentTarget: { value: boxId } }) => {

        this.setState({ boxId });
    };

    onClickImportFromWordpress = () => {

        this.setState({ commentsToUpload: [], statuses: [] }, () => {

            const reader = new FileReader();
            reader.onerror = err => {
                this.setState({ errorMessage: err.message });
            };
            reader.onload = () => {

                try {

                    const parser = new DOMParser();
                    const xmlDoc = parser.parseFromString(reader.result, 'application/xml');

                    if (xmlDoc.documentElement.nodeName === 'parsererror') {

                        throw new Error('Error while parsing.');
                    }

                    const items = xmlDoc.getElementsByTagName('item');
                    const commentsToUpload = [];
                    const refs = {};

                    for (let i = 0; i < items.length; i++) {

                        const post = items[i];
                        const linkTag = post.getElementsByTagName('link');
                        const comments = post.getElementsByTagName('wp:comment');


                        if (!linkTag[0]) {
                            throw new Error('Link tag is missing.');
                        }
                        const url = linkTag[0].textContent;
                        const urlLocation = document.createElement('a');
                        const domain = this.props.domain.toLowerCase();
                        urlLocation.href = url.toLowerCase();
                        urlLocation.hash = this.state.boxId || 'commentbox';
                        if (!(urlLocation.host.indexOf(domain) === 0 || urlLocation.host.indexOf(`www.${domain}`) === 0)) {
                            throw new Error(`The page this comment was found is not part of this project's domain.`);
                        }


                        for (let x = 0; x < comments.length; x++) {

                            const comment = comments[x];
                            const tags = {
                                originalId: comment.getElementsByTagName('wp:comment_id'),
                                originalParentId: comment.getElementsByTagName('wp:comment_parent'),
                                body: comment.getElementsByTagName('wp:comment_content'),
                                userId: comment.getElementsByTagName('wp:comment_user_id'),
                                userDisplayName: comment.getElementsByTagName('wp:comment_author'),
                                createdAt: comment.getElementsByTagName('wp:comment_date_gmt'),
                                userEmail: comment.getElementsByTagName('wp:comment_author_email'),
                                approved: comment.getElementsByTagName('wp:comment_approved')

                            };

                            const a = document.createElement('a');
                            a.setAttribute('href', urlLocation.href);
                            if (a.pathname.length === 0 || a.pathname.charAt(a.pathname.length - 1) !== '/') {
                                a.pathname = a.pathname + '/';
                            }

                            const pageId = SparkMD5.hash(a.href.replace(a.protocol, ''));

                            const data = {
                                type: 'wordpress',
                                url: urlLocation.href,
                                pageId
                            };

                            Object.keys(tags).forEach(key => {

                                if (!tags[key][0]) {
                                    throw new Error(`Tag for ${key} is missing.`);
                                }
                                data[key] = tags[key][0].textContent;
                            });

                            if (parseInt(data.approved) !== 1) {
                                continue;
                            }

                            const hash = SparkMD5.hash(data.userEmail.toLowerCase().trim());

                            if (!data.userId || parseInt(data.userId) === 0) {
                                data.userId = '';
                            } else {
                                data.userId = SparkMD5.hash(data.userId);
                            }

                            data.userPhotoURL = `https://www.gravatar.com/avatar/${hash}?d=404`;
                            delete data.userEmail;
                            delete data.approved;

                            if (!refs[data.originalParentId]) {
                                refs[data.originalParentId] = [];
                            }
                            refs[data.originalParentId].push(data);
                        }
                    }

                    const makeIds = (parent = null) => {

                        const parentOriginalId = parent ? parent.originalId : '0';
                        const parentId = parent ? `${parent.id}/` : '';

                        if (!refs[parentOriginalId]) {
                            return;
                        }

                        refs[parentOriginalId].forEach(data => {

                            data.id = `${parentId}${uuid.v4()}`;
                            const pieces = data.id.split('/');
                            if (pieces.length >= 10) {
                                const id = pieces.pop(); pieces.pop();
                                pieces.push(id);
                                data.id = pieces.join('/');
                            }

                            commentsToUpload.push(data);
                            makeIds(data);
                        });
                    };

                    makeIds();

                    this.setState({ commentsToUpload }, this.uploadComments);


                } catch (err) {

                    this.setState({ errorMessage: err.message });
                }
            };

            reader.readAsText(this.state.file);
        });
    };

    onClickImportFromDisqus = () => {

        this.setState({ commentsToUpload: [], statuses: [] }, () => {

            const reader = new FileReader();
            reader.onerror = err => {
                this.setState({ errorMessage: err.message });
            };
            reader.onload = () => {

                try {

                    const parser = new DOMParser();
                    const xmlDoc = parser.parseFromString(reader.result, 'application/xml');

                    if (xmlDoc.documentElement.nodeName === 'parsererror') {

                        throw new Error('Error while parsing.');
                    }

                    const threads = xmlDoc.getElementsByTagName('thread');
                    const locations = {};

                    for (let i = 0; i < threads.length; i++) {

                        const thread = threads[i];

                        if (thread.children.length === 0) {
                            continue;
                        }

                        const id = getRequiredAttribute(thread, 'dsq:id');
                        const url = getElementContentByTag(thread, 'link', true);
                        const urlLocation = document.createElement('a');
                        const domain = this.props.domain.toLowerCase();
                        urlLocation.href = url.toLowerCase();
                        urlLocation.hash = this.state.boxId || 'commentbox';
                        if (!(urlLocation.host.indexOf(domain) === 0 || urlLocation.host.indexOf(`www.${domain}`) === 0)) {
                            throw new Error(`The page this comment was found is not part of this project's domain.`);
                        }
                        locations[id] = urlLocation;
                    }


                    const items = xmlDoc.getElementsByTagName('post');
                    const commentsToUpload = [];
                    const refs = {};

                    for (let i = 0; i < items.length; i++) {

                        const comment = items[i];
                        const id = comment.getAttribute('dsq:id', true);
                        const threadTag = comment.getElementsByTagName('thread');
                        const threadId = getRequiredAttribute(threadTag[0], 'dsq:id');
                        const urlLocation = locations[threadId];
                        const parentTag = comment.getElementsByTagName('parent');
                        const parentId = parentTag[0] ? getRequiredAttribute(parentTag[0], 'dsq:id') : '0';
                        const authorTag = comment.getElementsByTagName('author');
                        const author = authorTag[0];
                        if (!author) {
                            throw new Error('Author not found');
                        }

                        const isDeleted = getElementContentByTag(comment, 'isDeleted') || 'false';
                        const isApproved = getElementContentByTag(comment, 'isApproved') || 'true';
                        if (isDeleted === 'true' || isApproved === 'false') {
                            continue;
                        }

                        const a = document.createElement('a');
                        a.setAttribute('href', urlLocation.href);
                        if (a.pathname.length === 0 || a.pathname.charAt(a.pathname.length - 1) !== '/') {
                            a.pathname = a.pathname + '/';
                        }

                        const pageId = SparkMD5.hash(a.href.replace(a.protocol, ''));

                        const data = {
                            originalId: id,
                            originalParentId: parentId,
                            body: getElementContentByTag(comment, 'message'),
                            userId: getElementContentByTag(author, 'username'),
                            userDisplayName: getElementContentByTag(author, 'name'),
                            createdAt: getElementContentByTag(comment, 'createdAt', true),
                            type: 'disqus',
                            url: urlLocation.href,
                            pageId
                        };

                        data.userPhotoURL = data.userId ? `https://disqus.com/api/users/avatars/${data.userId}.jpg` : '';

                        if (!data.userId) {
                            data.userId = '';
                        } else {
                            data.userId = SparkMD5.hash(data.userId);
                        }

                        if (!refs[data.originalParentId]) {
                            refs[data.originalParentId] = [];
                        }
                        refs[data.originalParentId].push(data);
                    }

                    const makeIds = (parent = null) => {

                        const parentOriginalId = parent ? parent.originalId : '0';
                        const parentId = parent ? `${parent.id}/` : '';

                        if (!refs[parentOriginalId]) {
                            return;
                        }

                        refs[parentOriginalId].forEach(data => {

                            data.id = `${parentId}${uuid.v4()}`;
                            const pieces = data.id.split('/');
                            if (pieces.length >= 10) {
                                const id = pieces.pop(); pieces.pop();
                                pieces.push(id);
                                data.id = pieces.join('/');
                            }

                            commentsToUpload.push(data);
                            makeIds(data);
                        });
                    };

                    makeIds();

                    this.setState({ commentsToUpload }, this.uploadComments);


                } catch (err) {

                    this.setState({ errorMessage: err.message });
                }
            };

            reader.readAsText(this.state.file);
        });

    };

    uploadComments = () => {

        const ref = importStorage
            .ref()
            .child(this.props.userId)
            .child(this.props.projectId);

        return Promise.all(this.state.commentsToUpload.map(customMetadata => {

            const { pageId, type } = customMetadata;

            delete customMetadata.pageId;
            delete customMetadata.type;

            return ref
                .child(pageId)
                .child(type)
                .child(SparkMD5.hash(customMetadata.originalId))
                .putString('', firebase.storage.StringFormat.RAW, { customMetadata })
                .then(() => 'uploaded')
                .catch(err => {

                    if (err.code !== 'storage/unauthorized') {

                        throw err;
                    }
                    return 'exists';
                })
                .then(status => {

                    this.setState(({ statuses }) => ({ statuses: statuses.concat(status) }));
                })
                .catch(err => {

                    this.setState(({ statuses }) => {

                        return {
                            errorMessage: err.message,
                            statuses: statuses.concat('error')
                        };
                    });
                });

        }));
    };


    get error() {


        if (!this.state.errorMessage) {
            return null;
        }

        return (
            <div className="alert alert-danger" role="alert">
                {this.state.errorMessage}
            </div>
        );
    }

    get progress() {

        if (!this.state.commentsToUpload || !this.state.statuses) {

            return null;
        }

        const numSuccess = this.state.statuses.filter(status => status === 'uploaded').length;
        const numWarning = this.state.statuses.filter(status => status === 'exists').length;
        const numError = this.state.statuses.filter(status => status === 'error').length;

        if (this.state.statuses.length === this.state.commentsToUpload.length) {
            return (
                <div className="alert alert-info" role="alert">
                    <p>
                        Import complete!
                    </p>
                    <ul>
                        <li>
                            {numSuccess} new comments added.
                        </li>
                        <li>
                            {numWarning} comments already existed.
                        </li>
                        <li>
                            {numError} comments had errors.
                        </li>
                    </ul>
                    <sub>
                       Note: Importing is asynchronous, so comments may take up to a few minutes to appear.
                    </sub>
                </div>
            );
        }

        const success = Math.round(numSuccess / this.state.commentsToUpload.length * 100);
        const warning = Math.round(numWarning / this.state.commentsToUpload.length * 100);
        const error = Math.round(numError / this.state.commentsToUpload.length * 100);

        return (
            <div className="progress">
                <div className="progress-bar progress-bar-striped active progress-bar-success" style={{width: `${success}%`}}>
                    <span className="sr-only">{success}% Complete (success)</span>
                </div>
                <div className="progress-bar progress-bar-striped active progress-bar-warning" style={{width: `${warning}%`}}>
                    <span className="sr-only">{warning}% Complete (warning)</span>
                </div>
                <div className="progress-bar progress-bar-striped active progress-bar-danger" style={{width: `${error}%`}}>
                    <span className="sr-only">{error}% Complete (danger)</span>
                </div>
            </div>
        );
    }

    render() {
        return (
            <div className="import">

                <div className="panel panel-default">
                    <div className="panel-body">

                        <fieldset>
                            <legend>
                                Import External Comments <a target="_blank" href="https://commentbox.io/docs/dashboard#importing-comments">Learn more <span className="glyphicon glyphicon-share-alt" /></a>
                            </legend>

                            <div className="row">
                                <div className="col-lg-8 col-lg-offset-2 col-md-10 col-md-offset-1">

                                    {this.error}

                                    <div className="form-group">

                                        <label htmlFor="file">
                                            Select a file
                                        </label>
                                        <input id="file" type="file" onChange={this.onChangeFile} />

                                    </div>

                                    <div className="form-group">

                                        <label htmlFor="box-id">
                                            Box ID (optional)
                                        </label>
                                        <input
                                            id="box-id"
                                            type="text"
                                            className="form-control"
                                            onChange={this.onChangeBoxId}
                                            value={this.state.boxId}
                                            placeholder="commentbox"
                                        />

                                    </div>

                                    <div className="form-group">

                                        <button
                                            type="button"
                                            className="btn btn-default"
                                            disabled={!this.state.file || !!this.state.commentsToUpload}
                                            onClick={this.onClickImportFromWordpress}
                                        >
                                            Import from Wordpress
                                        </button>

                                        <button
                                            type="button"
                                            className="btn btn-default pull-right"
                                            disabled={!this.state.file || !!this.state.commentsToUpload}
                                            onClick={this.onClickImportFromDisqus}
                                        >
                                            Import from Disqus
                                        </button>

                                    </div>

                                    <div className="form-group">
                                        {this.progress}
                                    </div>

                                </div>
                            </div>

                        </fieldset>

                    </div>
                </div>

            </div>
        );
    }
}

export default Import;


// WEBPACK FOOTER //
// ./src/components/projects/Import.jsx